import axios, { AxiosResponse } from 'axios';
import { Zone, ZoneCategory, ZoneTemplate, ZoneStats, ZoneFilters } from '../shared/types/zone';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
});

// Request interceptor for auth tokens
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      console.warn('Authentication required');
    }
    return Promise.reject(error);
  }
);

// Zones API functions
export const zonesApi = {
  // Get zones for a specific drawing
  async getZones(drawingId: string, projectId?: string): Promise<Zone[]> {
    try {
      const response: AxiosResponse<{ success: boolean; data: Zone[] }> = await api.get(`/zones`, {
        params: { drawingId, projectId }
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch zones:', error);
      throw new Error(`Failed to fetch zones: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Create new zone
  async createZone(zoneData: Partial<Zone>): Promise<Zone> {
    try {
      const response: AxiosResponse<{ success: boolean; data: Zone }> = await api.post('/zones', zoneData);
      return response.data.data;
    } catch (error) {
      console.error('Failed to create zone:', error);
      throw new Error(`Failed to create zone: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Update zone
  async updateZone(zoneId: string, zoneData: Partial<Zone>): Promise<Zone> {
    try {
      const response: AxiosResponse<{ success: boolean; data: Zone }> = await api.put(`/zones/${zoneId}`, zoneData);
      return response.data.data;
    } catch (error) {
      console.error('Failed to update zone:', error);
      throw new Error(`Failed to update zone: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Delete zone
  async deleteZone(zoneId: string): Promise<void> {
    try {
      await api.delete(`/zones/${zoneId}`);
    } catch (error) {
      console.error('Failed to delete zone:', error);
      throw new Error(`Failed to delete zone: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get zone categories
  async getZoneCategories(): Promise<ZoneCategory[]> {
    try {
      const response: AxiosResponse<{ success: boolean; data: ZoneCategory[] }> = await api.get('/zones/categories');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch zone categories:', error);
      throw new Error(`Failed to fetch zone categories: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get zone templates
  async getZoneTemplates(): Promise<ZoneTemplate[]> {
    try {
      const response: AxiosResponse<{ success: boolean; data: ZoneTemplate[] }> = await api.get('/zones/templates');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch zone templates:', error);
      throw new Error(`Failed to fetch zone templates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get zone statistics
  async getZoneStats(drawingId: string, projectId?: string): Promise<ZoneStats> {
    try {
      const response: AxiosResponse<{ success: boolean; data: ZoneStats }> = await api.get('/zones/stats', {
        params: { drawingId, projectId }
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch zone stats:', error);
      throw new Error(`Failed to fetch zone stats: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Search zones with filters
  async searchZones(filters: ZoneFilters, drawingId: string, projectId?: string): Promise<Zone[]> {
    try {
      const response: AxiosResponse<{ success: boolean; data: Zone[] }> = await api.post('/zones/search', {
        filters,
        drawingId,
        projectId
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to search zones:', error);
      throw new Error(`Failed to search zones: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
};

// Utility functions for data validation and formatting
export const validateZoneData = (zoneData: Partial<Zone>): string[] => {
  const errors: string[] = [];
  
  if (!zoneData.name || zoneData.name.trim().length === 0) {
    errors.push('Zone name is required');
  }
  
  if (!zoneData.coordinates || zoneData.coordinates.length < 3) {
    errors.push('Zone must have at least 3 coordinate points');
  } else {
    // Validate coordinates are within bounds
    for (const coord of zoneData.coordinates) {
      if (coord.x < 0 || coord.x > 1 || coord.y < 0 || coord.y > 1) {
        errors.push('All coordinates must be between 0 and 1');
        break;
      }
    }
  }
  
  if (!zoneData.drawingId) {
    errors.push('Drawing ID is required');
  }
  
  if (!zoneData.projectId) {
    errors.push('Project ID is required');
  }
  
  return errors;
};

export const formatZoneForBackend = (zoneData: Partial<Zone>): any => {
  return {
    name: zoneData.name?.trim(),
    description: zoneData.description?.trim(),
    color: zoneData.color || '#6b7280',
    coordinates: zoneData.coordinates || [],
    drawingId: zoneData.drawingId,
    projectId: zoneData.projectId,
    isActive: zoneData.isActive ?? true,
    tags: zoneData.tags || [],
    category: zoneData.category
  };
};

export const validateZoneCoordinates = (coordinates: { x: number; y: number }[]): boolean => {
  if (coordinates.length < 3) return false;
  
  // Check if all coordinates are within bounds
  return coordinates.every(coord => 
    coord.x >= 0 && coord.x <= 1 && coord.y >= 0 && coord.y <= 1
  );
};

export default zonesApi;
